import { Component, Inject, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CheckoutService } from '../../../../services/checkout/checkout.service';
import { CouponService } from '../../../../services/coupon/coupon.service';
import { PaymentTierService } from '../../../../services/payment-tier/payment-tire.service';
import { ExemptedEmbService } from '../../../../services/exempted-emb/exempted-emb.service';
import { DbService } from '../../../../services/db/db.service';
import { environment } from '../../../../../environments/environment';

export interface PaymentModalData {
  applicationId: string;
  businessName?: string;
  exemptedEmb?: any;
  formData?: any;
}

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrl: './payment-modal.component.css'
})
export class PaymentModalComponent implements OnInit {
  paymentForm: FormGroup;
  couponCodeControl = new FormControl('');
  
  computedPrice: number | null = null;
  originalPrice: number | null = null;
  discountAmount: number = 0;
  
  appliedCoupon: any = null;
  couponError: string = '';
  validatingCoupon: boolean = false;
  isProcessing: boolean = false;
  
  exemptedEmb: any = null;
  turnoverAmount: number = 0;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PaymentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentModalData,
    private toastr: ToastrService,
    private checkoutService: CheckoutService,
    private couponService: CouponService,
    private paymentTierService: PaymentTierService,
    private exemptedEmbService: ExemptedEmbService,
    private dbService: DbService
  ) {
    this.paymentForm = this.fb.group({
      numberOfSubscriptionYear: ['', Validators.required]
    });
  }

  async ngOnInit() {
    // Load application data and initialize form
    await this.loadApplicationData();
    
    // Initialize exempted status
    if (this.data.exemptedEmb) {
      this.exemptedEmb = this.data.exemptedEmb;
    } else {
      await this.checkExemptedStatus();
    }
    
    // Load any existing coupon data
    this.loadExistingCouponData();
    
    // If year is already selected and we have turnover data, calculate price
    const currentYear = this.paymentForm.get('numberOfSubscriptionYear')?.value;
    if (currentYear && this.turnoverAmount) {
      await this.calculatePrice(currentYear, this.turnoverAmount);
    }
  }

  async loadApplicationData() {
    try {
      if (!this.data.formData) {
        // Load form data from the database
        const formData = await this.dbService.getCertificationForm(this.data.applicationId);
        this.data.formData = formData;
      }
      
      // Extract turnover value for price calculation
      if (this.data.formData?.businessProfileForm?.last_year_gross_turnover_euro) {
        this.turnoverAmount = this.data.formData.businessProfileForm.last_year_gross_turnover_euro;
      } 
      
      // Set business name if not provided
      if (!this.data.businessName && this.data.formData?.businessIdentificationForm?.business_name) {
        this.data.businessName = this.data.formData.businessIdentificationForm.business_name;
      }
    } catch (error) {
      console.error('Error loading application data:', error);
      this.toastr.error('Failed to load application data');
    }
  }

  async checkExemptedStatus() {
    try {
      // Get the business email from form data to check exemption
      const businessEmail = this.data.formData?.businessIdentificationForm?.email;
      if (businessEmail) {
        const exemptedData = await firstValueFrom(this.exemptedEmbService.getExemptedEmbByEmail(businessEmail));
        this.exemptedEmb = exemptedData;
      }
    } catch (error) {
      console.error('Error checking exempted status:', error);
    }
  }

  onYearChange() {
    const years = this.paymentForm.get('numberOfSubscriptionYear')?.value;
    if (years && this.turnoverAmount) {
      this.calculatePrice(years, this.turnoverAmount);
      
      // If a coupon is applied, recalculate the discount with the new price
      if (this.appliedCoupon) {
        this.applyCurrentCouponDiscount();
      }
    }
  }

  paymentTiers: any[] = [];
  
  // Mapping for turnover values (in case old data uses indices)
  turnoverMapping = [
    "less_than_1_million",     // index 0
    "1m_to_5m",               // index 1
    "5m_to_10m",              // index 2
    "10m_to_50m",             // index 3
    "50m_to_100m",            // index 4
    "100m_and_above"          // index 5
  ];

  async calculatePrice(years: number, turnover: any) {
    try {
      // Load payment tiers if not already loaded
      if (this.paymentTiers.length === 0) {
        this.paymentTiers = await firstValueFrom(this.paymentTierService.getPaymentTiers()) || [];
      }

      if (years && turnover !== null && turnover !== undefined && this.paymentTiers.length > 0) {
        const numberOfYears = +years;
        
        
        const selectedTier = this.paymentTiers.find(
          (tier) => tier.value === turnover
        );
        
        if (selectedTier) {
          const basePrice = Math.round(numberOfYears * selectedTier.price * 10) / 10;
          this.originalPrice = basePrice;
          this.computedPrice = basePrice;
          
          // Recalculate coupon discount if applied
          if (this.appliedCoupon) {
            this.applyCurrentCouponDiscount();
          }
        }
      } 
    } catch (error) {
      console.error('Error calculating price:', error);
      this.toastr.error('Failed to calculate price');
    }
  }

  async validateCoupon() {
    const couponCode = this.couponCodeControl.value?.trim();
    if (!couponCode) return;

    this.validatingCoupon = true;
    this.couponError = '';

    try {
      // Determine application type based on form data
      const isRenewal = this.data.formData?.status?.isRenewal || false;
      const applicationType = isRenewal ? 'renewal_applications' : 'new_applications';

      // Switch to realtime validation; unsubscribe on each new attempt via local subscription
      this.couponService.watchCouponValidation(couponCode, applicationType).subscribe({
        next: async (result) => {
          if (result.isValid && result.coupon) {
            this.appliedCoupon = result.coupon;
            this.couponError = '';
            
            // Disable the coupon input field
            this.couponCodeControl.disable();
            
            this.applyCurrentCouponDiscount();
            
            // Immediately save coupon data to database
            try {
              await this.saveCouponToDatabase();
            } catch (error) {
              console.error('Error saving coupon to database:', error);
              // Revert local changes if database save fails
              this.appliedCoupon = null;
              this.computedPrice = this.originalPrice;
              this.discountAmount = 0;
              this.couponCodeControl.enable();
            }
          } else {
            this.couponError = result.message || 'Invalid coupon';
            this.appliedCoupon = null;
          }
          this.validatingCoupon = false;
        },
        error: (error) => {
          console.error('Error validating coupon:', error);
          this.couponError = 'Failed to validate coupon. Please try again.';
          this.appliedCoupon = null;
          this.validatingCoupon = false;
        }
      });
    } catch (error) {
      console.error('Error validating coupon:', error);
      this.couponError = 'Failed to validate coupon. Please try again.';
      this.appliedCoupon = null;
      this.validatingCoupon = false;
    }
  }

  applyCurrentCouponDiscount() {
    if (!this.appliedCoupon || !this.originalPrice) return;

    const discountCalculation = (this.originalPrice * this.appliedCoupon.discountPercentage) / 100;
    this.discountAmount = this.truncateToDecimals(discountCalculation, 2);
    
    const discountedPrice = this.originalPrice - this.discountAmount;
    this.computedPrice = this.truncateToDecimals(discountedPrice, 2);
  }

  async removeCoupon() {
    try {
      // Clear local coupon data
      this.appliedCoupon = null;
      this.couponError = '';
      this.discountAmount = 0;
      this.computedPrice = this.originalPrice;
      
      // Clear the input field and re-enable it
      this.couponCodeControl.setValue('');
      this.couponCodeControl.enable();
      
      // Immediately update database to remove coupon data
      await this.removeCouponFromDatabase();
      
    } catch (error) {
      console.error('Error removing coupon from database:', error);
      this.toastr.error('Failed to remove coupon. Please try again.');
    }
  }

  clearCouponValidation() {
    this.couponError = '';
    // If no coupon is applied, ensure the control is enabled
    if (!this.appliedCoupon) {
      this.couponCodeControl.enable();
    }
  }

  loadExistingCouponData() {
    // Load any previously applied coupon data
    if (this.data.formData?.appliedCoupon) {
      
      const existingCoupon = this.data.formData.appliedCoupon;
      
      // Validate essential fields exist
      if (!existingCoupon.couponName || existingCoupon.discountPercentage === undefined || existingCoupon.discountPercentage === null) {

        this.appliedCoupon = null;
        return;
      }
      
      // Map the stored coupon data to our expected format with only valid values
      this.appliedCoupon = {
        couponName: existingCoupon.couponName,
        discountPercentage: existingCoupon.discountPercentage,
        status: existingCoupon.status || 'active',
        usageCount: existingCoupon.usageCount || 0,
        createdDate: existingCoupon.createdDate || '',
        updatedDate: existingCoupon.updatedDate || ''
      };
      
      // Add optional fields only if they are properly defined
      if (existingCoupon.couponId && existingCoupon.couponId !== undefined && existingCoupon.couponId !== null) {
        this.appliedCoupon.id = existingCoupon.couponId;
      } else if (existingCoupon.id && existingCoupon.id !== undefined && existingCoupon.id !== null) {
        this.appliedCoupon.id = existingCoupon.id;
      }
      
      if (existingCoupon.applicationType && existingCoupon.applicationType !== undefined && existingCoupon.applicationType !== null) {
        this.appliedCoupon.applicationType = existingCoupon.applicationType;
      }
      
      // Populate the coupon input field and ensure it's disabled
      this.couponCodeControl.setValue(this.appliedCoupon.couponName);
      this.couponCodeControl.disable();
      
      // Recalculate price with existing coupon if we have the necessary data
      if (this.originalPrice) {
        this.applyCurrentCouponDiscount();
      }
    } else {
      this.appliedCoupon = null;
      this.couponCodeControl.setValue('');
    }
  }

  async saveCouponToDatabase() {
    try {
      if (!this.appliedCoupon) return;
      
      const formData = { ...this.data.formData };
      
      // Save coupon data to form data - ensure no undefined values
      if (!this.appliedCoupon.couponName || this.appliedCoupon.discountPercentage === undefined) {
        console.warn('Cannot save incomplete coupon data:', this.appliedCoupon);
        throw new Error('Coupon data is incomplete and cannot be saved');
      }
      
      const couponData: any = {
        couponName: this.appliedCoupon.couponName,
        discountPercentage: this.appliedCoupon.discountPercentage
      };
      
      // Add optional fields only if they exist and are not null/undefined
      if (this.appliedCoupon.id && this.appliedCoupon.id !== undefined && this.appliedCoupon.id !== null) {
        couponData.couponId = this.appliedCoupon.id;
      }
      if (this.appliedCoupon.applicationType && this.appliedCoupon.applicationType !== undefined && this.appliedCoupon.applicationType !== null) {
        couponData.applicationType = this.appliedCoupon.applicationType;
      }
      
      formData.appliedCoupon = couponData;
      console.log('Saving coupon data to database:', couponData);
      
      // Update the certification form in the database
      await this.dbService.updateCertificationForm(this.data.applicationId, formData);
      
      // Update local data reference
      this.data.formData = formData;
      
    } catch (error) {
      console.error('Error saving coupon to database:', error);
      throw error;
    }
  }

  async removeCouponFromDatabase() {
    try {
      await this.dbService.removeCouponDataFromCertification(this.data.applicationId);

      // Update local data reference to reflect removals
      if (this.data.formData) {
        delete this.data.formData.appliedCoupon;
        delete this.data.formData.couponCode;
        delete this.data.formData.couponDiscount;
        delete this.data.formData.originalPrice;
        delete this.data.formData.discountAmount;
        delete this.data.formData.couponId;
        delete this.data.formData.couponName;
        delete this.data.formData.discountPercentage;
        delete this.data.formData.applicationType;
      }

    } catch (error) {
      console.error('Error removing coupon from database:', error);
      throw error;
    }
  }

  truncateToDecimals(value: number, decimals: number): number {
    const multiplier = Math.pow(10, decimals);
    return Math.floor(value * multiplier) / multiplier;
  }

  truncatePrice(price: number): string {
    return this.truncateToDecimals(price, 2).toFixed(2);
  }

  async proceedToPayment() {
    if (!this.paymentForm.valid || this.computedPrice === null) {
      this.toastr.error('Please complete all required fields');
      return;
    }

    this.isProcessing = true;

    try {
      // Update the form with subscription year before payment
      await this.updateApplicationWithPaymentData();
      
      // Save the form with SUBMITTED status
      await this.updateFormStatus('SUBMITTED');
      
      // Small delay to ensure database writes are committed before backend reads
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      
      // Proceed to checkout
      const response = await this.checkoutService.onStripeCheckout(this.data.applicationId);
      
      if (response) {
        // Close dialog and let the checkout service handle the redirection
        this.dialogRef.close({ success: true, sessionId: response });
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      this.toastr.error('Failed to process payment. Please try again.');
      this.isProcessing = false;
    }
  }

  async updateApplicationWithPaymentData() {
    try {
      // Fetch fresh data from database to ensure we have the latest state
      const freshFormData = await this.dbService.getCertificationForm(this.data.applicationId);
      if (!freshFormData) {
        throw new Error('Failed to fetch current application data');
      }
      
      // Update subscription year
      freshFormData.numberOfSubscriptionYear = this.paymentForm.get('numberOfSubscriptionYear')?.value;
      
      // Ensure coupon data consistency with current UI state
      if (this.appliedCoupon && this.appliedCoupon.couponName && this.appliedCoupon.discountPercentage !== undefined) {
        console.log('Saving coupon data to database:', this.appliedCoupon);
        
        // Create a clean coupon object with only defined values
        const couponData: any = {
          couponName: this.appliedCoupon.couponName,
          discountPercentage: this.appliedCoupon.discountPercentage
        };
        
        // Add optional fields only if they are defined and not null
        if (this.appliedCoupon.id && this.appliedCoupon.id !== undefined && this.appliedCoupon.id !== null) {
          couponData.couponId = this.appliedCoupon.id;
        }
        if (this.appliedCoupon.applicationType && this.appliedCoupon.applicationType !== undefined && this.appliedCoupon.applicationType !== null) {
          couponData.applicationType = this.appliedCoupon.applicationType;
        }
        
        freshFormData.appliedCoupon = couponData;
        console.log('Final coupon data being saved:', couponData);
      } else {
        // No coupon is currently applied - remove ALL coupon-related data
        const couponFieldsToRemove = [
          'appliedCoupon',
          'couponCode', 
          'couponDiscount',
          'originalPrice',
          'discountAmount',
          'couponId',
          'couponName',
          'discountPercentage',
          'applicationType'
        ];
        
        let removedFields: string[] = [];
        couponFieldsToRemove.forEach(field => {
          if (freshFormData.hasOwnProperty(field)) {
            delete freshFormData[field];
            removedFields.push(field);
          }
        });
        
      }

      // Update the certification form
      await this.dbService.updateCertificationForm(this.data.applicationId, freshFormData);
      
      // Update local reference for consistency
      this.data.formData = freshFormData;
      
    } catch (error) {
      console.error('Error updating application with payment data:', error);
      throw error;
    }
  }

  async updateFormStatus(status: string) {
    try {
      // Use the fresh data that was just updated
      const formData = { ...this.data.formData };
      
      // Update status
      if (!formData.status) {
        formData.status = {
          application_id: this.data.applicationId,
          current_status: status,
          status_datetime: new Date().toISOString(),
          status_history: []
        };
      } else {
        formData.status.current_status = status;
        formData.status.status_datetime = new Date().toISOString();
      }

      // Add to status history if not already present
      const existingSubmitted = formData.status.status_history?.find((h: any) => h.status === 'SUBMITTED');
      if (!existingSubmitted && status === 'SUBMITTED') {
        if (!formData.status.status_history) {
          formData.status.status_history = [];
        }
        formData.status.status_history.push({
          status: 'SUBMITTED',
          datetime: new Date().toISOString()
        });
      }

      await this.dbService.updateCertificationForm(this.data.applicationId, formData);
    } catch (error) {
      console.error('Error updating form status:', error);
      throw error;
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }
}
